import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { questionAnswered, questionNotAnswered, questionErrored } from 'actions/questionnaires';
import dynamicUpdateProp from 'common/questionnaires/questionDataProp';
import { Mode, DynamicUpdateAction } from 'common/questionnaires/types';
import { getTinErrorMessage } from 'common/questionnaires/tinHelper';

const QuestionFields = ({ id, name, isForm, runValidation, fields, dispatch, dynamicUpdate, mode, hasError, isNumericOnly, answer, customerName, derivedName, customerRecordNameIsDRE, maxValue }) => {
  const [inputs, setInputs] = useState((new Array(fields.length)).fill({ value: '', changed: false }));
  const regex = new RegExp('^[+-]?([0-9]+([.][0-9]*)?|[.][0-9]+)$');
  let isValid = true;

  const setName = (nameInput, nameToSet) => {
    const listOfNames = nameToSet.split(' ');
    if (name === 'firstName') {
      // eslint-disable-next-line
      nameInput[0].value = listOfNames[0];
    } else if (name === 'lastName' && listOfNames.length > 1) {
      // eslint-disable-next-line
      nameInput[0].value = listOfNames[listOfNames.length - 1];
    } else if (name === 'middleName' && listOfNames.length > 2) {
      nameInput[0].value = ''; // Reset any existing values.
      for (let i = 1; i < listOfNames.length - 1; i += 1) {
        // eslint-disable-next-line
        nameInput[0].value = nameInput[0].value.concat(' ', listOfNames[i]);
      }
    } else if (name !== 'firstName' && name !== 'lastName' && name !== 'middleName') {
      nameInput[0].value = nameToSet;
    }
  };

  let fullName = '';
  inputs.forEach(({ value }, i) => {
    if (i === 0 && value) {
      fullName = value;
    } else if (value && value !== '') {
      fullName += ` ${ value.trim() }`;
    }
  });

  if (inputs && inputs.length > 0) {
    if (answer && answer === 'CUSTOMER-NAME') {
      if (customerRecordNameIsDRE && derivedName.length > 0) {
        setName(inputs, derivedName);
      } else {
        setName(inputs, customerName);
      }
    }
    if (answer && answer === 'DRE-NAME' && customerRecordNameIsDRE && customerName.length > 0 && derivedName.length > 0
    ) {
      setName(inputs, customerName);
    }
    if (inputs[0].value.length > 0 && isForm && fields[0].validation !== 'ftin') {
      dispatch(questionAnswered({ id, answer: inputs[0].value, mode, isForm, name }));
    }
  }

  const checkMaxValue = (requiredFieldsComplete, index) => {
    if (inputs[index].value && inputs.length > 0) {
      const fieldValue = parseInt(inputs[index].value, 10);
      if (fieldValue > maxValue) {
        return false;
      }
    }
    return requiredFieldsComplete;
  };

  const validateQuestion = (shouldSubmitAnswer) => {
    let requiredFieldsComplete = true;
    let requiredFieldsChanged = true;
    let customError = null;
    fields.forEach(({ required }, i) => {
      if (required && inputs[i] && (!inputs[i].value || inputs[i].value.trim() === '')) {
        requiredFieldsComplete = false;
      }
      if (!runValidation && required && inputs[i] && !inputs[i].changed) {
        requiredFieldsChanged = false;
      }

      // TODO: this is a temporary measure, need to reconsider design for validating FTIN
      if (fields && fields[0].validation && fields[0].validation === 'ftin' && runValidation) {
        customError = getTinErrorMessage(inputs[i].value, null, required);
        requiredFieldsComplete = !customError;
      }

      if (isNumericOnly && maxValue !== null) {
        requiredFieldsComplete = checkMaxValue(requiredFieldsComplete, i);
      }
    });

    if (requiredFieldsComplete) {
      if (shouldSubmitAnswer) {
        dispatch(questionAnswered({ id, answer: fullName, mode, isForm, name }));
      } else {
        dispatch(questionNotAnswered({ id, mode, isForm }));
      }
    } else if (requiredFieldsChanged || shouldSubmitAnswer) {
      dispatch(questionErrored({ id, mode, isForm, customError }));
    }
    return customError === null;
  };

  useEffect(() => {
    isValid = validateQuestion(isForm && runValidation);
  }, [inputs, runValidation, dynamicUpdate]);

  useEffect(() => {
    if (inputs[0].value.length > 0 && isForm && isValid) {
      dispatch(questionAnswered({ id, answer: inputs[0].value, mode, isForm, name }));
    }
  }, [inputs]);

  function handleChange(fieldIndex, event) {
    if (isNumericOnly) {
      if (event.target.value !== '' && !regex.test(event.target.value)) {
        return;
      }
    }

    setInputs(inputs.map((fieldInput, i) => (i === fieldIndex ? {
      changed: true,
      value: event.target.value
    } : fieldInput)));

    let requiredFieldsComplete = true;
    fields.forEach(({ required }, i) => {
      if (required && inputs[i] && (!inputs[i].value || inputs[i].value.trim() === '')) {
        requiredFieldsComplete = false;
      }
    });

    if (inputs.length <= 1 && (event.target.value !== '' || requiredFieldsComplete)) {
      dispatch(questionAnswered({ id, answer: event.target.value, mode, isForm, name }));
    } else if (event.target.value !== '' && requiredFieldsComplete) {
      dispatch(questionAnswered({ id, answer: event.target.value, mode, isForm, name }));
    }
  }

  const disabled = (dynamicUpdate && dynamicUpdate.conditionMet && dynamicUpdate.action === DynamicUpdateAction.DisableAndClear);
  return (<div className="fields-container">
    <div className="inputs-container">
      {fields && fields.length !== 0 && fields.map(({ placeholder }, i) => <input
        id={ id }
        name={ name }
        value={ inputs && inputs[i].value && !disabled ? inputs[i].value : '' }
        onChange={ e => handleChange(i, e) }
        key={ placeholder }
        placeholder={ placeholder }
        autoComplete="no"
        className={ fields.length === 3 ? 'w-32' : '' }
        disabled={ dynamicUpdate && dynamicUpdate.conditionMet && dynamicUpdate.action === DynamicUpdateAction.DisableAndClear }
        type="text"
      />)}
    </div>
    {!isForm && <button className="btn questionnaire-button continue" type="button" disabled={ hasError } onClick={ () => validateQuestion(true) }>Next</button>}
  </div>);
};

QuestionFields.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string,
  isForm: PropTypes.bool,
  runValidation: PropTypes.bool,
  fields: PropTypes.arrayOf(PropTypes.shape({
    placeholder: PropTypes.string,
    required: PropTypes.bool,
    validation: PropTypes.string
  })).isRequired,
  dispatch: PropTypes.func.isRequired,
  dynamicUpdate: dynamicUpdateProp,
  mode: PropTypes.number.isRequired,
  hasError: PropTypes.bool.isRequired,
  isNumericOnly: PropTypes.bool,
  answer: PropTypes.string,
  customerName: PropTypes.string,
  derivedName: PropTypes.string,
  customerRecordNameIsDRE: PropTypes.bool,
  maxValue: PropTypes.number,
};

QuestionFields.defaultProps = {
  isForm: false,
  runValidation: false,
  dynamicUpdate: null,
  name: null,
  isNumericOnly: false,
  answer: null,
  customerName: '',
  derivedName: '',
  customerRecordNameIsDRE: false,
  maxValue: null
};

const mapDispatchToProps = dispatch => ({ dispatch });

const mapStateToProps = (state) => {
  const {
    app: { onlineConfig },
    preQualifyingQuestionnaire: { derivedName, customerRecordNameIsDRE },
    formQuestionnaire: { runValidation }
  } = state;

  return {
    mode: (onlineConfig && onlineConfig.preQualifyingQuestionnaireMode) ? onlineConfig.preQualifyingQuestionnaireMode : Mode.Crs,
    runValidation,
    customerName: onlineConfig && onlineConfig.customerName,
    derivedName,
    customerRecordNameIsDRE
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(QuestionFields);
